import React, { useState, useEffect, useRef } from 'react';
import { getMessages, sendMessage, deleteMessage, getFriends, removeFriend, subscribeToMessages, subscribeToFriends, cleanupOldMessages, subscribeToUserChats, createPrivateChat, addParticipantToChat, deletePrivateChat, subscribeToBannedUsers, createGroupChat, markChatAsRead, getUserProfile, subscribeToUserProfile } from '../services/firestore.js'; // Removed getEmoteByCode
import { Send, Users, Trash2, UserMinus, MessageSquare, Plus, UserPlus, Smile, CheckSquare, Square, X } from 'lucide-react';
import EmotePicker from '../components/EmotePicker.jsx';
import UserBadge from '../components/UserBadge.jsx';
import { useEmoteTheme } from '../hooks/useEmoteTheme.js';
import { useAppearance } from '../hooks/useAppearance.js';

export default function Chat({ onViewProfile, user }) {
    const { getEmoteUrl } = useEmoteTheme();
    const { appearance } = useAppearance();
    const { fontSize, emoteSize } = appearance.chat;
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState("");

    // Safety check
    if (!user) return React.createElement('div', { className: 'glass-panel', style: { padding: '20px', textAlign: 'center' } }, "Loading...");


    // UI State
    const [activeTab, setActiveTab] = useState('friends'); // 'friends' | 'chats'
    const [activeChatId, setActiveChatId] = useState('general-room');
    const [activeChatName, setActiveChatName] = useState('General Room');
    const [sidebarOpen, setSidebarOpen] = useState(true); // Default open for better discovery

    // Data State
    const [friends, setFriends] = useState([]);
    const [myChats, setMyChats] = useState([]);
    const [bannedUserIds, setBannedUserIds] = useState([]);
    const [chatsLoaded, setChatsLoaded] = useState(false);
    const [liveProfiles, setLiveProfiles] = useState({}); // Real-time profile cache
    const profileListeners = useRef({}); // Track active listeners

    const [confirmDeleteId, setConfirmDeleteId] = useState(null);
    const [confirmRemoveFriendId, setConfirmRemoveFriendId] = useState(null);
    const [showAddMember, setShowAddMember] = useState(false);
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);

    // Group Chat Creation State
    const [isCreatingGroup, setIsCreatingGroup] = useState(false);
    const [selectedFriendsForGroup, setSelectedFriendsForGroup] = useState([]);

    const bottomRef = useRef(null);

    // Initial Load & Subscriptions
    useEffect(() => {
        if (!user?.uid) return;

        // 1. Subscribe to Friends
        const unsubscribeFriends = subscribeToFriends(user.uid, (updatedFriends) => {
            setFriends(updatedFriends);
        });

        // 2. Subscribe to My Chats
        const unsubscribeChats = subscribeToUserChats(user.uid, (chats) => {
            setMyChats(chats);
            setChatsLoaded(true);
        });

        // 3. Subscribe to Banned Users
        const unsubscribeBanned = subscribeToBannedUsers((bannedIds) => {
            setBannedUserIds(bannedIds);
        });

        // 4. Maintenance (Lazy) - Only Admins can clean up to avoid permission errors
        if (user?.isAdmin || user?.isSuperAdmin) {
            cleanupOldMessages(3).catch(console.error);
        }

        return () => {
            unsubscribeFriends();
            unsubscribeChats();
            unsubscribeBanned();
        };
    }, [user]);

    // Safety: Redirect to General Room if removed from active chat
    useEffect(() => {
        if (activeChatId === 'general-room') return;
        if (!chatsLoaded) return; // Wait for initial load

        const stillInChat = myChats.find(c => c.id === activeChatId);
        if (!stillInChat) {
            setActiveChatId('general-room');
            setActiveChatName('General Room');
        }
    }, [myChats, activeChatId, chatsLoaded]);

    // Live Profile Subscriptions for Chat Participants
    useEffect(() => {
        if (!messages.length) return;

        const uniqueSenders = [...new Set(messages.map(m => m.senderId))];

        uniqueSenders.forEach(id => {
            if (id && id !== user?.uid && !profileListeners.current[id]) {
                // Subscribe if not already listening
                // Note: We subscribe to EVERYONE in the chat to ensure live badge updates.
                // Even friends, because 'friends' list might have stale snapshots.

                profileListeners.current[id] = subscribeToUserProfile(id, (profile) => {
                    if (profile) {
                        setLiveProfiles(prev => ({ ...prev, [id]: profile }));
                    }
                });
            }
        });

        // Cleanup function for unmount (we keep listeners active during chat session for performance)
    }, [messages, user?.uid]); // removed friends dependency to avoid re-subscribing logic churn

    // Cleanup listeners on unmount
    useEffect(() => {
        return () => {
            Object.values(profileListeners.current).forEach(unsub => unsub());
            profileListeners.current = {};
        };
    }, []);

    // Active Chat Message Subscription
    useEffect(() => {
        if (!user?.uid) return;
        setMessages([]); // Clear previous messages while loading

        const unsubscribeMessages = subscribeToMessages(activeChatId, user.uid, (updatedMessages) => {
            // Check for private chat time-boxing (security/privacy)
            if (activeChatId !== 'general-room') {
                const currentChat = myChats.find(c => c.id === activeChatId);
                // If I am a participant, find my join time
                if (currentChat && currentChat.participants && currentChat.participants[user.uid]) {
                    const myJoinTime = currentChat.participants[user.uid].joinedAt?.seconds || 0;

                    // Filter messages sent before I joined
                    const validMessages = updatedMessages.filter(msg => {
                        const msgTime = msg.timestamp?.seconds || 0;
                        // Always show my own messages even if distinct (edge case)
                        if (msg.senderId === user.uid) return true;
                        // Show if message is newer than my join time
                        return msgTime >= myJoinTime;
                    });
                    setMessages(validMessages);
                    return;
                }
            }

            // Default (General Room or Fallback)
            setMessages(updatedMessages);
        });

        return () => unsubscribeMessages();
    }, [activeChatId, user, myChats]); // Re-run when switching chats or chats update

    // Auto-scroll
    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    const handleSend = async (e) => {
        e.preventDefault();
        if (!input.trim() || !user?.uid) return;

        try {
            await sendMessage(activeChatId, user.uid, user.name, input, user.subscriptionTier);
            setInput("");
            setShowEmojiPicker(false); // Close emoji picker after sending
        } catch (error) {
            console.error('Error sending message:', error);
        }
    };

    const handleEmojiSelect = (emojiId, emojiChar) => {
        setInput(prev => prev + emojiChar);
        // Keep picker open for multiple emoji selections
    };

    const handleDeleteMessage = async (msgId, isAuthor) => {
        if (!user?.uid) return;
        if (confirmDeleteId === msgId) {
            try {
                await deleteMessage(activeChatId, msgId, user.uid, isAuthor);
                setConfirmDeleteId(null);
            } catch (error) {
                console.error('Error deleting message:', error);
            }
        } else {
            setConfirmDeleteId(msgId);
            setTimeout(() => setConfirmDeleteId(null), 3000);
        }
    };

    const handleStartChat = async (friend) => {
        const existingChat = myChats.find(c =>
            c.type === 'private' &&
            c.participantIds.length === 2 &&
            c.participantIds.includes(friend.id) &&
            c.participantIds.includes(user.uid)
        );

        if (existingChat) {
            setActiveChatId(existingChat.id);
            setActiveChatName(friend.name); // Simple name
            setActiveTab('chats');
            markChatAsRead(existingChat.id, user.uid);
        } else {
            try {
                const newChatId = await createPrivateChat(user.uid, friend.id);
                setActiveChatId(newChatId);
                setActiveChatName(friend.name);
                setActiveTab('chats');
            } catch (e) {
                console.error("Failed to create chat", e);
            }
        }
    };

    const handleCreateGroupChat = async () => {
        if (selectedFriendsForGroup.length < 2) {
            alert("Please select at least 2 friends for a group chat.");
            return;
        }

        const targetParticipantIds = [user.uid, ...selectedFriendsForGroup].sort();

        // Check compatibility
        // Check compatibility
        const existingChat = myChats.find(c => {
            if (c.type !== 'private') return false;
            const currentIds = [...c.participantIds].sort();
            return JSON.stringify(currentIds) === JSON.stringify(targetParticipantIds);
        });

        if (existingChat) {
            setActiveChatId(existingChat.id);
            setActiveTab('chats');
            setIsCreatingGroup(false);
            setSelectedFriendsForGroup([]);
            markChatAsRead(existingChat.id, user.uid);
        } else {
            try {
                const newChatId = await createGroupChat(user.uid, selectedFriendsForGroup);
                setActiveChatId(newChatId);
                setActiveTab('chats');
                setIsCreatingGroup(false);
                setSelectedFriendsForGroup([]);
            } catch (e) {
                console.error("Failed to create group chat", e);
                alert("Failed to create group chat. Please try again.");
            }
        }
    };

    const toggleFriendSelection = (friendId) => {
        if (selectedFriendsForGroup.includes(friendId)) {
            setSelectedFriendsForGroup(selectedFriendsForGroup.filter(id => id !== friendId));
        } else {
            setSelectedFriendsForGroup([...selectedFriendsForGroup, friendId]);
        }
    };

    const handleAddMember = async (friendId) => {
        if (!user?.uid || activeChatId === 'general-room') return;
        try {
            await addParticipantToChat(activeChatId, friendId);
            setShowAddMember(false);
            alert("Friend added to chat!");
        } catch (e) {
            console.error(e);
        }
    };

    const handleDeleteChat = async () => {
        if (!user?.uid || activeChatId === 'general-room') return;
        if (window.confirm("Are you sure you want to delete this chat? This cannot be undone.")) {
            try {
                await deletePrivateChat(activeChatId);
                setActiveChatId('general-room');
                setActiveChatName('General Room');
            } catch (e) {
                console.error("Error deleting chat:", e);
                alert("Failed to delete chat.");
            }
        }
    };

    // Helper to get chat name
    const getChatDisplayName = (chat) => {
        if (chat.type !== 'private') return "General Room";

        // Filter out me
        const otherIds = chat.participantIds.filter(id => id !== user.uid);
        if (otherIds.length === 0) return "Just You";

        // Resolve names from Friends list
        const names = otherIds.map(id => {
            const f = friends.find(fr => fr.id === id);
            return f ? f.name : "Unknown";
        });

        return names.join(", ");
    };

    const isHost = () => {
        if (activeChatId === 'general-room') return false;
        const chat = myChats.find(c => c.id === activeChatId);
        return chat && chat.hostId === user.uid;
    };

    return React.createElement('div', { style: { display: 'flex', gap: '20px', height: 'calc(100vh - 140px)' } },
        // Main Chat Area
        React.createElement('div', { className: 'glass-panel', style: { flex: 1, display: 'flex', flexDirection: 'column' } },
            // Header
            React.createElement('div', { style: { padding: '15px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' } },
                React.createElement('div', { style: { display: 'flex', flexDirection: 'column' } },
                    React.createElement('span', { style: { fontWeight: 'bold' } },
                        activeChatId === 'general-room' ? "General Room" : getChatDisplayName(myChats.find(c => c.id === activeChatId) || { participantIds: [], type: 'private' })
                    ),
                    activeChatId !== 'general-room' && React.createElement('span', { style: { fontSize: '10px', opacity: 0.5 } }, "Private Group")
                ),
                React.createElement('div', { style: { display: 'flex', gap: '10px' } },
                    // Add Member Button (Only for Private Chats)
                    activeChatId !== 'general-room' && isHost() && React.createElement('button', {
                        className: 'btn-ghost',
                        onClick: () => setShowAddMember(!showAddMember),
                        title: "Add Friend to Chat"
                    }, React.createElement(UserPlus, { size: 20 })),

                    // Delete Chat Button (Host Only)
                    activeChatId !== 'general-room' && isHost() && React.createElement('button', {
                        className: 'btn-ghost',
                        onClick: handleDeleteChat,
                        title: "Delete Chat (Host Only)",
                        style: { color: '#ef4444' }
                    }, React.createElement(Trash2, { size: 20 })),

                    // Sidebar Toggle
                    React.createElement('button', {
                        className: 'btn-ghost',
                        onClick: () => setSidebarOpen(!sidebarOpen),
                        style: { color: sidebarOpen ? 'var(--primary)' : 'inherit', position: 'relative' }
                    },
                        React.createElement(Users, { size: 20 }),
                        !sidebarOpen && myChats.reduce((acc, chat) => acc + (chat.participants?.[user.uid]?.unreadCount || 0), 0) > 0 && React.createElement('span', {
                            style: {
                                position: 'absolute',
                                top: '-5px',
                                right: '-5px',
                                background: 'var(--secondary)',
                                color: 'white',
                                fontSize: '10px',
                                padding: '2px 4px',
                                borderRadius: '10px',
                                fontWeight: 'bold',
                                minWidth: '16px',
                                textAlign: 'center',
                                border: '2px solid var(--bg-card)' // outline to separate from icon
                            }
                        }, myChats.reduce((acc, chat) => acc + (chat.participants?.[user.uid]?.unreadCount || 0), 0))
                    )
                )
            ),

            // Messages
            React.createElement('div', { style: { flex: 1, overflowY: 'auto', padding: '20px', display: 'flex', flexDirection: 'column', gap: '15px' } },
                messages
                    .filter(msg => {
                        // 1. Check Ban Status
                        const isSenderBanned = bannedUserIds.includes(msg.senderId);
                        const isAdmin = user?.isAdmin || user?.isSuperAdmin;
                        if (isSenderBanned && !isAdmin) {
                            return false; // Hide banned content for regular users
                        }

                        // 2. General Room Logic (Friendship Time Filter)
                        if (activeChatId === 'general-room') {
                            if (msg.senderId === user?.uid) return true;
                            const friend = friends.find(f => f.id === msg.senderId);
                            if (!friend) return false;
                            const msgTime = msg.timestamp?.seconds || 0;
                            const friendTime = friend.friendshipCreatedAt?.seconds || 0;
                            if (msgTime > 0 && friendTime > 0) return msgTime >= friendTime;
                            return true;
                        }
                        // Private Chat Logic (Handled in Subscription mostly, but extra safety here optional)
                        return true;
                    })
                    .map(msg => {
                        const isSenderBanned = bannedUserIds.includes(msg.senderId);
                        const isAdmin = user?.isAdmin || user?.isSuperAdmin;
                        const isContentDimmed = isSenderBanned && isAdmin;

                        return React.createElement('div', {
                            key: msg.id,
                            className: "message-bubble",
                            style: {
                                alignSelf: msg.senderId === user?.uid ? 'flex-end' : 'flex-start',
                                maxWidth: '70%',
                                background: msg.senderId === user?.uid ? 'var(--chat-bg-out)' : 'var(--chat-bg-in)',
                                padding: '12px 16px',
                                borderRadius: '16px',
                                borderBottomRightRadius: msg.senderId === user?.uid ? '4px' : '16px',
                                borderBottomLeftRadius: msg.senderId !== user?.uid ? '4px' : '16px',
                                opacity: isContentDimmed ? 0.6 : 1,
                                border: isContentDimmed ? '1px solid #ef4444' : '1px solid transparent',
                                boxShadow: msg.senderId === user?.uid ? '0 0 10px rgba(139, 92, 246, 0.1)' : 'none'
                            }
                        },
                            React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', marginBottom: '4px' } },
                                React.createElement('button', {
                                    onClick: () => onViewProfile && onViewProfile(msg.senderId),
                                    style: {
                                        fontSize: '12px',
                                        opacity: 0.7,
                                        fontWeight: '600',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '5px',
                                        background: 'none',
                                        border: 'none',
                                        color: 'inherit',
                                        padding: 0,
                                        cursor: 'pointer',
                                        textDecoration: 'underline',
                                        textDecorationColor: 'transparent',
                                        transition: 'text-decoration-color 0.2s'
                                    },
                                    onMouseEnter: (e) => e.target.style.textDecorationColor = 'currentColor',
                                    onMouseLeave: (e) => e.target.style.textDecorationColor = 'transparent'
                                },
                                    (() => {
                                        if (msg.senderId === user?.uid) return user.name;
                                        // Use Live Profile -> Friend -> Message Fallback
                                        const live = liveProfiles[msg.senderId];
                                        const friend = friends.find(f => f.id === msg.senderId);
                                        return live ? live.name : (friend ? friend.name : (msg.senderName || msg.sender));
                                    })(),
                                    // Subscription Badge (Real-Time)
                                    (() => {
                                        let tier;
                                        if (msg.senderId === user?.uid) {
                                            tier = user.subscriptionTier;
                                        } else {
                                            // Priority: Live Profile (Best) -> Friend (Good) -> Message Snapshot (Fallback)
                                            const live = liveProfiles[msg.senderId];
                                            const friend = friends.find(f => f.id === msg.senderId);
                                            tier = live ? live.subscriptionTier : (friend ? friend.subscriptionTier : msg.senderTier);
                                        }
                                        return React.createElement(UserBadge, { tier: tier, size: 18, style: { marginLeft: '4px' } });
                                    })(),
                                    isContentDimmed && React.createElement('span', { style: { fontSize: '8px', color: '#ef4444', border: '1px solid #ef4444', padding: '1px 3px', borderRadius: '3px', marginLeft: '4px' } }, "BANNED")
                                ),
                                React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: '8px' } },
                                    React.createElement('button', {
                                        onClick: () => handleDeleteMessage(msg.id, msg.senderId === user?.uid),
                                        title: confirmDeleteId === msg.id ? "Click to Confirm" : (msg.senderId === user?.uid ? "Unsend" : "Delete for Me"),
                                        className: "delete-btn",
                                        style: {
                                            background: confirmDeleteId === msg.id ? 'var(--secondary)' : 'none',
                                            borderRadius: '4px',
                                            border: 'none',
                                            color: confirmDeleteId === msg.id ? 'white' : 'inherit',
                                            cursor: 'pointer',
                                            padding: confirmDeleteId === msg.id ? '2px 6px' : '0',
                                            fontSize: confirmDeleteId === msg.id ? '10px' : 'inherit',
                                            opacity: confirmDeleteId === msg.id ? 1 : undefined
                                        }
                                    }, confirmDeleteId === msg.id ? "Sure?" : React.createElement(Trash2, { size: 12 })),
                                    React.createElement('div', { style: { fontSize: '10px', opacity: 0.5 } },
                                        msg.timestamp ? new Date(msg.timestamp.seconds * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : "..."
                                    )
                                )
                            ),
                            React.createElement('div', { style: { fontSize: `${fontSize}px` } }, parseMessageContent(msg.content || msg.text, getEmoteUrl, emoteSize))
                        );
                    }),
                React.createElement('div', { ref: bottomRef })
            ),

            // Input
            React.createElement('form', { onSubmit: handleSend, style: { padding: '20px', borderTop: '1px solid var(--border)', display: 'flex', gap: '10px', position: 'relative' } },
                showEmojiPicker && React.createElement(EmotePicker, {
                    user: user,
                    userTier: user.subscriptionTier,
                    onSelect: (code) => {
                        setInput(prev => prev + code);
                        // setShowEmojiPicker(false); // Optional: keep open for chaining
                    }
                }),
                React.createElement('input', {
                    value: input,
                    onChange: (e) => setInput(e.target.value),
                    placeholder: "Type a message...",
                    style: { flex: 1, background: 'rgba(0,0,0,0.2)', border: '1px solid var(--border)', padding: '12px', borderRadius: '12px', color: 'var(--text-main)', outline: 'none' }
                }),
                React.createElement('button', {
                    type: 'button',
                    onClick: () => setShowEmojiPicker(!showEmojiPicker),
                    className: 'btn-ghost',
                    style: {
                        padding: '12px',
                        color: showEmojiPicker ? 'var(--primary)' : '#999',
                        transition: 'color 0.2s'
                    },
                    title: 'Add Emoji'
                },
                    React.createElement(Smile, { size: 20 })
                ),
                React.createElement('button', { type: 'submit', className: 'btn', style: { padding: '12px' } },
                    React.createElement(Send, { size: 20 })
                ),
                // Emote Picker
                showEmojiPicker && React.createElement(EmotePicker, {
                    user: user,
                    userTier: user?.subscriptionTier || 'global',
                    onSelect: (code) => {
                        setInput(prev => prev + code);
                    },
                    onClose: () => setShowEmojiPicker(false)
                })
            )
        ),

        // Sidebar (Collapsible)
        sidebarOpen && React.createElement('div', { className: 'glass-panel animate-fade-in', style: { width: '280px', display: 'flex', flexDirection: 'column' } },
            // Sidebar Tabs
            React.createElement('div', { style: { display: 'flex', padding: '10px', gap: '10px', borderBottom: '1px solid var(--border)' } },
                React.createElement('button', {
                    onClick: () => setActiveTab('friends'),
                    style: { flex: 1, padding: '8px', borderRadius: '8px', border: 'none', background: activeTab === 'friends' ? 'var(--primary)' : 'transparent', color: activeTab === 'friends' ? 'white' : 'var(--text-muted)', cursor: 'pointer' }
                }, "Friends"),
                React.createElement('button', {
                    onClick: () => setActiveTab('chats'),
                    style: { flex: 1, padding: '8px', borderRadius: '8px', border: 'none', background: activeTab === 'chats' ? 'var(--primary)' : 'transparent', color: activeTab === 'chats' ? 'white' : 'var(--text-muted)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }
                },
                    "Chats",
                    myChats.reduce((acc, chat) => acc + (chat.participants?.[user.uid]?.unreadCount || 0), 0) > 0 && React.createElement('span', {
                        style: {
                            background: 'var(--secondary)',
                            color: 'white',
                            fontSize: '10px',
                            padding: '2px 6px',
                            borderRadius: '10px',
                            fontWeight: 'bold'
                        }
                    }, myChats.reduce((acc, chat) => acc + (chat.participants?.[user.uid]?.unreadCount || 0), 0))
                )
            ),

            // Content Area (Scrollable)
            React.createElement('div', { style: { flex: 1, overflowY: 'auto', padding: '10px', display: 'flex', flexDirection: 'column' } },

                // --- FRIENDS LIST ---
                activeTab === 'friends' && friends.map(friend => {
                    const isSelected = selectedFriendsForGroup.includes(friend.id);
                    return React.createElement('div', {
                        key: friend.id,
                        onClick: () => isCreatingGroup ? toggleFriendSelection(friend.id) : null,
                        style: {
                            display: 'flex', alignItems: 'center', gap: '10px', padding: '10px', borderRadius: '8px',
                            cursor: isCreatingGroup ? 'pointer' : 'default',
                            transition: 'background 0.2s', marginBottom: '5px',
                            background: isSelected ? 'rgba(139, 92, 246, 0.2)' : 'transparent',
                            border: isSelected ? '1px solid var(--primary)' : '1px solid transparent'
                        },
                        onMouseEnter: (e) => e.currentTarget.style.background = isSelected ? 'rgba(139, 92, 246, 0.2)' : 'rgba(255,255,255,0.05)',
                        onMouseLeave: (e) => e.currentTarget.style.background = isSelected ? 'rgba(139, 92, 246, 0.2)' : 'transparent'
                    },
                        isCreatingGroup && React.createElement('div', { style: { marginRight: '5px', color: isSelected ? 'var(--primary)' : '#666' } },
                            isSelected ? React.createElement(CheckSquare, { size: 18 }) : React.createElement(Square, { size: 18 })
                        ),
                        React.createElement('img', { src: friend.avatar, style: { width: '32px', height: '32px', borderRadius: '50%' } }),
                        React.createElement('div', { style: { flex: 1 } },
                            React.createElement('div', { style: { fontWeight: '500' } }, friend.name),
                            React.createElement('div', { style: { fontSize: '10px', opacity: 0.5 } }, "Online")
                        ),
                        // Actions (Hide in selection mode)
                        !isCreatingGroup && React.createElement('div', { style: { display: 'flex', gap: '5px' } },
                            React.createElement('button', {
                                onClick: (e) => { e.stopPropagation(); handleStartChat(friend); },
                                className: 'btn-ghost',
                                title: "Message",
                                style: { padding: '6px', color: 'var(--primary)' }
                            }, React.createElement(MessageSquare, { size: 16 })),
                            React.createElement('button', {
                                onClick: (e) => { e.stopPropagation(); onViewProfile && onViewProfile(friend.id); },
                                className: 'btn-ghost',
                                title: "View Profile",
                                style: { padding: '6px' }
                            }, React.createElement(Users, { size: 16 }))
                        )
                    );
                }),

                // --- CHATS LIST ---
                activeTab === 'chats' && React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: '5px' } },
                    // General Room Pinned
                    React.createElement('div', {
                        onClick: () => { setActiveChatId('general-room'); setActiveChatName('General Room'); },
                        style: {
                            padding: '12px', borderRadius: '8px', cursor: 'pointer',
                            background: activeChatId === 'general-room' ? 'rgba(139, 92, 246, 0.2)' : 'rgba(255,255,255,0.05)',
                            border: activeChatId === 'general-room' ? '1px solid var(--primary)' : '1px solid transparent',
                            display: 'flex', alignItems: 'center', gap: '10px'
                        }
                    },
                        React.createElement(Users, { size: 20 }),
                        React.createElement('span', { style: { fontWeight: 'bold' } }, "General Room")
                    ),

                    // Private Chats
                    myChats.map(chat =>
                        React.createElement('div', {
                            key: chat.id,
                            onClick: () => { setActiveChatId(chat.id); setActiveChatName(getChatDisplayName(chat)); markChatAsRead(chat.id, user.uid); },
                            title: getChatDisplayName(chat),
                            style: {
                                padding: '12px', borderRadius: '8px', cursor: 'pointer',
                                background: activeChatId === chat.id ? 'rgba(139, 92, 246, 0.2)' : 'rgba(255,255,255,0.05)',
                                border: activeChatId === chat.id ? '1px solid var(--primary)' : '1px solid transparent',
                                display: 'flex', alignItems: 'center', gap: '10px'
                            }
                        },
                            (chat.participantIds.length > 2) ?
                                React.createElement('div', {
                                    style: {
                                        width: '20px', height: '20px',
                                        borderRadius: '6px',
                                        border: '1px solid currentColor',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        fontSize: '10px', fontWeight: 'bold'
                                    }
                                }, chat.participantIds.length) :
                                React.createElement(MessageSquare, { size: 20 }),
                            React.createElement('div', { style: { flex: 1, overflow: 'hidden' } },
                                React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' } },
                                    React.createElement('div', { style: { fontWeight: '600', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' } }, getChatDisplayName(chat)),
                                    (chat.participants?.[user.uid]?.unreadCount > 0) && React.createElement('span', {
                                        style: {
                                            background: 'var(--secondary)',
                                            color: 'white',
                                            fontSize: '10px',
                                            padding: '2px 6px',
                                            borderRadius: '10px',
                                            fontWeight: 'bold'
                                        }
                                    }, chat.participants[user.uid].unreadCount)
                                ),
                                React.createElement('div', { style: { fontSize: '10px', opacity: 0.5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' } },
                                    chat.lastMessage ? `${chat.lastMessage.senderId === user.uid ? 'You' : '...'}: ${chat.lastMessage.content} ` : "No messages yet"
                                )
                            )
                        )
                    )
                )
            ),

            // Fixed Footer Area (Create Group Button)
            activeTab === 'friends' && React.createElement('div', { style: { padding: '20px', borderTop: '1px solid var(--border)' } },
                !isCreatingGroup ? (
                    React.createElement('button', {
                        onClick: () => setIsCreatingGroup(true),
                        className: 'btn-ghost',
                        style: { width: '100%', padding: '12px', justifyContent: 'center', border: '1px dashed var(--border)', color: 'var(--text-muted)', borderRadius: '12px' }
                    }, React.createElement(Plus, { size: 16, style: { marginRight: '8px' } }), "Create Group Chat")
                ) : (
                    React.createElement('div', { style: { display: 'flex', gap: '10px' } },
                        React.createElement('button', {
                            onClick: () => { setIsCreatingGroup(false); setSelectedFriendsForGroup([]); },
                            className: 'btn-ghost',
                            style: { flex: 1, padding: '12px', justifyContent: 'center', borderRadius: '12px' }
                        }, "Cancel"),
                        React.createElement('button', {
                            onClick: handleCreateGroupChat,
                            className: 'btn',
                            disabled: selectedFriendsForGroup.length < 2,
                            style: { flex: 1, padding: '12px', justifyContent: 'center', opacity: selectedFriendsForGroup.length < 2 ? 0.5 : 1, borderRadius: '12px' }
                        }, "Create Group")
                    )
                )
            ),

            // Add Member Modal Area (Simple inline overlay)
            showAddMember && React.createElement('div', {
                style: { padding: '10px', borderTop: '1px solid var(--border)', background: 'rgba(0,0,0,0.3)' }
            },
                React.createElement('div', { style: { fontSize: '12px', marginBottom: '8px', fontWeight: 'bold' } }, "Add Friend to Group:"),
                React.createElement('div', { style: { maxHeight: '150px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '5px' } },
                    friends
                        .filter(f => !myChats.find(c => c.id === activeChatId)?.participantIds.includes(f.id))
                        .map(f =>
                            React.createElement('button', {
                                key: f.id,
                                onClick: () => handleAddMember(f.id),
                                className: 'btn-ghost',
                                style: { justifyContent: 'flex-start', padding: '6px', fontSize: '12px', background: 'rgba(255,255,255,0.05)' }
                            }, React.createElement(Plus, { size: 12, style: { marginRight: '6px' } }), f.name)
                        )
                ),
                React.createElement('button', {
                    onClick: () => setShowAddMember(false),
                    style: { width: '100%', marginTop: '5px', padding: '4px', background: 'transparent', border: 'none', color: '#999', fontSize: '10px', cursor: 'pointer' }
                }, "Cancel")
            )
        )
    );
}

// Helper to parse message content with emote support
const parseMessageContent = (content, getEmoteUrl, emoteSize = 28) => {
    if (!content) return null;

    // Regex for :emote_code:
    const parts = content.split(/(:[a-zA-Z0-9_]+:)/g);

    return React.createElement('span', { style: { wordBreak: 'break-word', lineHeight: '1.5' } },
        parts.map((part, index) => {
            if (part.startsWith(':') && part.endsWith(':')) {
                // Determine URL (reactive to theme)
                const url = getEmoteUrl ? getEmoteUrl(part) : null;

                if (url) {
                    return React.createElement('img', {
                        key: index,
                        src: url,
                        alt: part,
                        title: part,
                        style: { height: `${emoteSize}px`, verticalAlign: 'middle', margin: '0 2px' }
                    });
                }
            }
            return part;
        })
    );
};
